import RentalApplicationForm from '@/components/Forms/RentalApplicationForm';
import { rentalApplicationSchema } from '@/schemas/rentalApplication';
import { IncomeType, RentalApplicationFormValues } from '@/types/rentalApplication';
import { Box, Container, VStack } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import SubmissonCard from './SubmissonCard';
import { useCreateRentalApplication } from '@/hooks/rentalApplication';
import { SectionHeader } from '@/components/SectionHeader';

const RentalApplicationView = () => {
  const {
    register,
    watch,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<RentalApplicationFormValues>({
    resolver: zodResolver(rentalApplicationSchema),
    defaultValues: {
      smoker: false,
      hasCurrentLandlord: false,
      pets: '',
      children: '',
      additionalInfo: '',
      rentMin: '0',
      rentMax: '0',
      incomeMonthly: '0',
      incomeType: IncomeType.WORK,
      city: 'Västerås',
    },
  });

  const { mutate: createRentalApplication, isPending, isSuccess } = useCreateRentalApplication();

  const mapFormValues = (data: RentalApplicationFormValues) => {
    return {
      firstname: data.firstname,
      lastname: data.lastname,
      ssn: data.ssn,
      phone_number: data.phoneNumber,
      email: data.email,
      city: data.city,
      income: {
        monthly: parseInt(data.incomeMonthly),
        income_type: data.incomeType,
      },
      employer: data.employer,
      current_landlord: data.hasCurrentLandlord
        ? {
            name: data.landlordName!,
            phone_number: data.landlordPhone!,
          }
        : undefined,
      rent: {
        min: parseInt(data.rentMin),
        max: parseInt(data.rentMax),
      },
      smoker: data.smoker,
      pets: data.pets,
      children: data.children,
      additional_info: data.additionalInfo,
    };
  };

  const onSubmit = (data: RentalApplicationFormValues) => {
    const mappedData = mapFormValues(data);
    createRentalApplication(mappedData);
  };

  return (
    <Box bg="bg.subtle" minH="100vh">
      <Container maxW="breakpoint-xl" py={{ base: '12', md: '20' }}>
        <VStack gap={{ base: '12', md: '16' }}>
          {!isSuccess && (
            <SectionHeader
              eyebrow="Hitta ditt nya hem"
              heading="Hyresansökan"
              description="Fyll i formuläret nedan så kontaktar vi dig när vi har en lägenhet som passar dina önskemål."
            />
          )}
          {isSuccess ? (
            <SubmissonCard />
          ) : (
            <RentalApplicationForm
              control={control}
              watch={watch}
              register={register}
              onSubmit={onSubmit}
              errors={errors}
              handleSubmit={handleSubmit}
              isLoading={isPending}
            />
          )}
        </VStack>
      </Container>
    </Box>
  );
};

export default RentalApplicationView;
